<template>
	<div>
		<ui-modal :show="show" @update:show="$emit('update:show', $event)">
			<ui-panel>
				<ui-panel-title><div class="subtitle">Add Guest</div></ui-panel-title>
				<ui-panel-body>
					<ui-form ref="form">
						<div class="row">
							<div class="col-md-6">
								<ui-text-box-field
									label="First Name"
									:rules="rules.first_name"
									v-model="guest.first_name">
								</ui-text-box-field>
							</div>

							<div class="col-md-6">
								<ui-text-box-field
									label="Last Name"
									:rules="rules.last_name"
									v-model="guest.last_name">
								</ui-text-box-field>
							</div>

							<div class="col-md-6">
								<ui-text-box-field
									label="Email"
									:rules="rules.primary_email"
									v-model="guest.primary_email"
									@blur="checkEmailExists">
								</ui-text-box-field>
							</div>

							<div class="col-md-6">
								<ui-select-field
									label="Gender"
									v-model="guest.gender"
									:items="genderOptions"
									:rules="rules.gender">
								</ui-select-field>
							</div>
						</div>
					
					</ui-form>

					<p class="text-danger">All fields are mandatory</p>

				</ui-panel-body>

				<ui-panel-footer>
					<ui-button color="primary" :loading="saving" @click="save">Add Guest</ui-button>
					<ui-button color="text" :disabled="saving" @click="dismiss">Cancel</ui-button>
				</ui-panel-footer>
			</ui-panel>
		</ui-modal>

		<guest-selection-modal
			:show="showSelectionModal"
			:email="guest.primary_email"
			:guests="existingGuests"
			@selected="useExistingGuest"
			@cancel="cancelSelection"
			@update:show="showSelectionModal = $event" />
	</div>
</template>

<script>
import { mapMutations, mapActions } from 'vuex';
import validate from 'igolf/shared/validators';
import GuestSelectionModal from 'igolf/users/guests/comp/GuestSelectionModal.vue';
import alerts from 'igolf/shared/alerts';

const defaultGuest = {
	user_class_id: 0,
	first_name: '',
	last_name: '',
	primary_email: '',
	gender: '',
	user_type: 'guest',
};

export default {
	components: {
		GuestSelectionModal,
	},

	props: { 
		show: { type: Boolean, default: false },
	},

	data()
	{
		return {
			guest: { ...defaultGuest },
			showSelectionModal: false,
			existingGuests: [],
			checkingEmail: false,

			rules: {
				first_name: [ v => !!v || 'Please enter the guest\'s first name' ],
				last_name: [ v => !!v || 'Please enter the guest\'s last name' ],
				gender: [ v => !!v || 'Please select the guest\'s gender' ],
				primary_email: [ 
					v => !!v || 'Please enter the guest\'s email',
					v => validate.email(v) || 'Invalid email address',
				]
			},

			genderOptions: [
				{ text: '(select gender)', value: '' },
				{ text: 'Male', value: 'male' },
				{ text: 'Female', value: 'female' },
				{ text: 'Non-specified/Other', value: 'other' },
			],

			classes: [],

			saving: false,
			doesExpire: false,
		}
	},


	methods: {
		...mapMutations('teesheet', ['addTeetimePlayer']),
		...mapActions('teesheet', ['addPlayerToReservation']),

		resetForm()
		{
			this.guest = { ...defaultGuest };
			this.existingGuests = [];
			this.showSelectionModal = false;
		},

		async checkEmailExists()
		{
			// Only check if email is valid and present
			if (!this.guest.primary_email || !validate.email(this.guest.primary_email))
				return;

			this.checkingEmail = true;

			try
			{
				var result = await this.$api.post('/users/find_guests_by_email', { 
					email: this.guest.primary_email 
				});
				
				if (result.data.guests && result.data.guests.length > 0)
				{
					this.existingGuests = result.data.guests;
					this.showSelectionModal = true;
				}
			}
			catch(err)
			{
				// Silently fail - user can still try to save
				console.error('Error checking email:', err);
			}
			finally
			{
				this.checkingEmail = false;
			}
		},

		useExistingGuest(selectedGuest)
		{
			// Use the selected existing guest
			this.showSelectionModal = false;
			this.addPlayerToReservation({ ...selectedGuest, user_type: 'guest' });
			this.dismiss();
		},

		cancelSelection()
		{
			this.showSelectionModal = false;
			// User can continue editing or change email
		},

		async save()
		{
			// validate input
			if (!this.$refs.form.validate())
				return;

			// add guest to database and to reservation
			this.saving = true;
			try
			{
				// send new guest data to server
				var result = await this.$api.post('/users', { user: this.guest });

				// add guest to reservation
				this.addPlayerToReservation({ ...this.guest, id: result.data.user.id });
				this.dismiss();
			}
			catch(err)
			{
				if (err.response && err.response.status === 422)
				{
					const data = err.response.data || {};
					
					// Check if it's a duplicate email error - try to fetch existing guests
					if (data.error_fields?.primary_email || (data.errors && Array.isArray(data.errors) && data.errors.some(e => e.toLowerCase().includes('email') || e.toLowerCase().includes('taken'))))
					{
						try
						{
							const checkResult = await this.$api.post('/users/find_guests_by_email', { 
								email: this.guest.primary_email 
							});
							
							if (checkResult.data.guests && checkResult.data.guests.length > 0)
							{
								this.existingGuests = checkResult.data.guests;
								this.showSelectionModal = true;
								return;
							}
						}
						catch(checkErr)
						{
							console.error('Error checking for existing guests:', checkErr);
						}
					}
					
					// Extract error message
					const errorMessage = data.error || 
						(Array.isArray(data.errors) ? data.errors.join(', ') : data.errors) ||
						(data.error_fields && Object.entries(data.error_fields).map(([field, msgs]) => 
							`${field === 'primary_email' ? 'Email' : field}: ${Array.isArray(msgs) ? msgs.join(', ') : msgs}`
						).join(', ')) ||
						'Please check the form and try again.';
					
					await alerts.show(errorMessage, 'Error', 'error');
				}
				else
				{
					const errorMsg = err.response?.data?.error || err.response?.data?.message || 'Failed to create guest';
					await alerts.show(errorMsg, 'Error', 'error');
				}
			}
			finally
			{
				this.saving = false;
			}
		},

		dismiss()
		{
			this.resetForm();
			this.$emit('update:show', false);
		}
	}
}
</script>